import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

interface BusinessImage {
  id: string;
  url: string;
  caption?: string;
}

interface BusinessGalleryViewProps {
  images: BusinessImage[];
  className?: string;
}

export const BusinessGalleryView: React.FC<BusinessGalleryViewProps> = ({
  images,
  className = ''
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(-1);

  const handlePrevious = () => {
    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setSelectedImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (selectedImageIndex === -1) return;

    switch (e.key) {
      case 'ArrowLeft':
        handlePrevious();
        break;
      case 'ArrowRight':
        handleNext();
        break;
      case 'Escape':
        setSelectedImageIndex(-1);
        break;
    }
  };

  if (images.length === 0) {
    return null;
  }

  return (
    <>
      <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ${className}`}>
        {images.map((image, index) => (
          <div
            key={image.id}
            className="relative aspect-square cursor-pointer group"
            onClick={() => setSelectedImageIndex(index)}
          >
            <img
              src={image.url}
              alt={image.caption || `Gallery image ${index + 1}`}
              className="w-full h-full object-cover rounded-lg"
            />
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg" />
          </div>
        ))}
      </div>

      <Dialog 
        open={selectedImageIndex !== -1} 
        onOpenChange={() => setSelectedImageIndex(-1)}
      >
        <DialogContent 
          className="max-w-4xl w-full h-[80vh] flex flex-col p-0"
          onKeyDown={handleKeyDown}
        >
          <DialogHeader className="p-4">
            <DialogTitle>
              {images[selectedImageIndex]?.caption || `Image ${selectedImageIndex + 1} of ${images.length}`}
            </DialogTitle>
          </DialogHeader>

          {selectedImageIndex !== -1 && (
            <div className="relative flex-1 bg-black/5">
              <img
                src={images[selectedImageIndex].url}
                alt={images[selectedImageIndex].caption || `Gallery image ${selectedImageIndex + 1}`}
                className="w-full h-full object-contain"
              />

              {/* Navigation buttons */}
              <div className="absolute inset-0 flex items-center justify-between p-4">
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full bg-white/80 hover:bg-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevious();
                  }}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full bg-white/80 hover:bg-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNext();
                  }}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Close button */}
              <Button
                variant="outline"
                size="icon"
                className="absolute top-4 right-4 rounded-full bg-white/80 hover:bg-white"
                onClick={() => setSelectedImageIndex(-1)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}; 